import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import ClientModal from "../components/ClientModal";
import ClientTagsModal from "../components/ClientTagsModal";
import { tagPillClass } from "../components/clientTags";
import ConfirmDialog from "../components/ConfirmDialog";
import PlanLockedHint from "../components/PlanLockedHint";
import Toast from "../components/Toast";
import useClients, { CONSENT_PURPOSES, SORTS } from "../hooks/useClients";
import { useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBranches } from "../services/branchService";
import {
  deleteClient,
  exportClients,
  fetchClientTags,
} from "../services/clientService";
import { formatDate } from "../services/datetime";
import { hasFeature } from "../services/features";
import { can } from "../services/permissions";
import { labelFor } from "../services/enumLabels";


/**
 * BIZ-15 — find anybody this business has ever served.
 *
 * ## Two things about this screen are unlike every other list in the portal
 *
 * It pages by **cursor**, because the API does: there is no page 4 to jump to,
 * only the row after the last one shown. So the pager offers previous and next
 * and nothing else, and re-sorting starts again from the top — the cursor is an
 * anchor into one particular ordering.
 *
 * And the count is **opt-in**, fetched once per filter set rather than on every
 * page, because counting is the expensive half of what offset paging used to
 * give away free.
 *
 * ## What bulk select can and cannot do today
 *
 * Ticking rows gives a count and drives the export. The export is the API's,
 * and the API exports **what the filters match** rather than a list of ids —
 * so the button says so. Sending a campaign to a selection needs the
 * notification machinery in file 09; there is deliberately no button here
 * pretending otherwise.
 */
export default function Clients() {
  const { t, i18n } = useTranslation();

  const {
    clients,
    meta,
    total,
    filters,
    sort,
    direction,
    applyFilters,
    resetFilters,
    applySort,
    searchTerm,
    setSearchTerm,
    submitSearch,
    goNext,
    goPrevious,
    atStart,
    atEnd,
    loading,
    error,
    reload,
  } = useClients();

  const [tags, setTags] = useState([]);
  const [branches, setBranches] = useState([]);

  // Back from a client lands where the user was; a stale branch id falls back.
  useScrollRestoration(!loading);
  useValidOption(filters.branch, branches.length ? branches : null, () => applyFilters({ branch: "" }));
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  const user = getCurrentUser();
  const mayCreate = can(user, "clients.create");
  const mayUpdate = can(user, "clients.update");
  const mayDelete = can(user, "clients.delete");
  // Permission and plan: CSV export is the plan's reports_export.
  const exportInPlan = hasFeature(user, "reports_export");
  const mayExport = can(user, "clients.export") && exportInPlan;

  const loadTags = useCallback(() => {
    fetchClientTags()
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags, i18n.language]);

  useEffect(() => {
    fetchBranches(1, 100)
      .then(({ branches: found }) => setBranches(found))
      .catch(() => setBranches([]));
  }, [i18n.language]);

  const filtersApplied =
    Boolean(filters.search) ||
    filters.tags.length > 0 ||
    Boolean(filters.branch) ||
    Boolean(filters.notVisitedSince) ||
    Boolean(filters.consent) ||
    filters.registered !== "";

  const tagsById = useMemo(
    () => Object.fromEntries(tags.map((tag) => [tag.id, tag])),
    [tags]
  );

  /* --------------------------------------------------------------- selection */

  const pageIds = clients.map((client) => client.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

  const toggleRow = (id) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );

  const togglePage = () =>
    setSelected((current) =>
      allOnPageSelected
        ? current.filter((id) => !pageIds.includes(id))
        : [...new Set([...current, ...pageIds])]
    );

  /* ------------------------------------------------------------------ actions */

  const runExport = async () => {
    setExporting(true);
    try {
      const filename = await exportClients(filters);
      setToast({ type: "success", message: t("clients.exported", { filename }) });
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("clients.exportFailed")) });
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteClient(deleting.id);
      setToast({ type: "success", message: t("clients.deleted", { name: deleting.name }) });
      setSelected((current) => current.filter((id) => id !== deleting.id));
      setDeleting(null);
      reload();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("clients.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  /* ------------------------------------------------------------------ display */

  const money = (client) => {
    // With a currency chosen, the value in that currency only ("—" before a first visit).
    if (client.value) {
      if (client.value.amount == null) return "—";
      return `${client.value.symbol ?? client.value.currency} ${client.value.amount}`;
    }
    const value = client.lifetime_value ?? "0";
    const symbol = client.lifetime_currency?.symbol ?? client.lifetime_currency?.code;
    return symbol ? `${symbol} ${value}` : value;
  };

  const subtitle = () => {
    if (total != null) return t("clients.count", { count: total });
    return t("clients.subtitle");
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("clients.title")}</h1>
          <p className="text-sm text-ink-2">{subtitle()}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => setTagsOpen(true)}>
            {t("clients.tags.manage")}
          </button>
          {mayExport && (
            <button type="button" className="btn-secondary" disabled={exporting} onClick={runExport}>
              {exporting ? t("clients.exporting") : t("clients.export")}
            </button>
          )}
          {!exportInPlan && can(user, "clients.export") && (
            <PlanLockedHint feature="reports_export" label={t("clients.export")} />
          )}
          {mayCreate && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              {t("clients.new")}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
        className="card mb-4 space-y-3 p-4"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="field-label" htmlFor="client-search">
              {t("clients.filters.search")}
            </label>
            <input
              id="client-search"
              className="field-input"
              placeholder={t("clients.filters.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <label className="field-label" htmlFor="client-branch-filter">
              {t("clients.filters.branch")}
            </label>
            <select
              id="client-branch-filter"
              className="field-input"
              value={filters.branch}
              onChange={(e) => applyFilters({ branch: e.target.value })}
            >
              <option value="">{t("clients.filters.any")}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="field-label" htmlFor="client-lastvisit-filter">
              {t("clients.filters.notVisitedSince")}
            </label>
            <input
              id="client-lastvisit-filter"
              type="date"
              className="field-input"
              value={filters.notVisitedSince}
              onChange={(e) => applyFilters({ notVisitedSince: e.target.value })}
            />
          </div>

          <div>
            <label className="field-label" htmlFor="client-consent-filter">
              {t("clients.filters.consent")}
            </label>
            <div className="flex gap-2">
              <select
                id="client-consent-filter"
                className="field-input"
                value={filters.consent}
                onChange={(e) => applyFilters({ consent: e.target.value })}
              >
                <option value="">{t("clients.filters.any")}</option>
                {CONSENT_PURPOSES.map((purpose) => (
                  <option key={purpose} value={purpose}>
                    {labelFor("consent_purpose", purpose)}
                  </option>
                ))}
              </select>

              {/* Only means anything alongside a purpose. */}
              {filters.consent && (
                <select
                  className="field-input"
                  aria-label={t("clients.filters.consentGranted")}
                  value={filters.consentGranted}
                  onChange={(e) => applyFilters({ consentGranted: e.target.value })}
                >
                  <option value="yes">{t("clients.filters.consentYes")}</option>
                  <option value="no">{t("clients.filters.consentNo")}</option>
                </select>
              )}
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="client-registered-filter">
              {t("clients.filters.registered")}
            </label>
            <select
              id="client-registered-filter"
              className="field-input"
              value={filters.registered}
              onChange={(e) => applyFilters({ registered: e.target.value })}
            >
              <option value="">{t("clients.filters.any")}</option>
              <option value="1">{t("common.yes")}</option>
              <option value="0">{t("common.no")}</option>
            </select>
          </div>

          {(meta?.currencies?.length ?? 0) > 1 && (
            <div>
              <label className="field-label" htmlFor="client-currency-filter">
                {t("clients.filters.currency")}
              </label>
              <select
                id="client-currency-filter"
                className="field-input"
                value={filters.currency || meta?.currency || "all"}
                onChange={(e) => applyFilters({ currency: e.target.value })}
              >
                {meta.currencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
                <option value="all">{t("clients.filters.allCurrencies")}</option>
              </select>
            </div>
          )}

          <div>
            <label className="field-label" htmlFor="client-sort">
              {t("clients.filters.sort")}
            </label>
            <div className="flex gap-2">
              <select
                id="client-sort"
                className="field-input"
                value={sort}
                onChange={(e) => applySort(e.target.value, direction)}
              >
                {SORTS.map((option) => (
                  <option key={option} value={option}>
                    {t(`clients.sorts.${option}`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary"
                title={t("clients.filters.direction")}
                onClick={() => applySort(sort, direction === "asc" ? "desc" : "asc")}
              >
                {direction === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn-primary">
              {t("clients.filters.apply")}
            </button>
            {filtersApplied && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  resetFilters();
                }}
              >
                {t("clients.filters.clear")}
              </button>
            )}
          </div>
        </div>

        {/* Segments. A client must carry every tag chosen, which is what makes
            them useful for narrowing rather than widening. */}
        {tags.length > 0 && (
          <div>
            <p className="field-label">{t("clients.filters.tags")}</p>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => {
                const on = filters.tags.includes(tag.id);

                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() =>
                      applyFilters({
                        tags: on
                          ? filters.tags.filter((id) => id !== tag.id)
                          : [...filters.tags, tag.id],
                      })
                    }
                    className={`min-h-touch px-3 py-1 text-xs ${tagPillClass(tag, {
                      selected: on,
                      interactive: true,
                    })}`}
                    aria-pressed={on}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {/* What a selection can actually do today — see the note at the top. */}
      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent-line bg-accent-wash px-4 py-2 text-sm text-accent-text">
          <span>{t("clients.selected", { count: selected.length })}</span>
          <span className="flex flex-wrap items-center gap-3">
            {mayExport && (
              <>
                <span className="text-xs text-accent-text">{t("clients.exportScope")}</span>
                <button
                  type="button"
                  className="font-medium hover:underline"
                  disabled={exporting}
                  onClick={runExport}
                >
                  {t("clients.export")}
                </button>
              </>
            )}
            <button
              type="button"
              className="font-medium hover:underline"
              onClick={() => setSelected([])}
            >
              {t("clients.clearSelection")}
            </button>
          </span>
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : clients.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">
            {filtersApplied ? t("clients.noMatches") : t("clients.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label={t("clients.selectPage")}
                      checked={allOnPageSelected}
                      onChange={togglePage}
                    />
                  </th>
                  <th className="px-4 py-3">{t("clients.table.name")}</th>
                  <th className="px-4 py-3">{t("clients.table.contact")}</th>
                  <th className="px-4 py-3">{t("clients.table.visits")}</th>
                  <th className="px-4 py-3">{t("clients.table.lastVisit")}</th>
                  <th className="px-4 py-3">{t("clients.table.value")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={client.name}
                        checked={selected.includes(client.id)}
                        onChange={() => toggleRow(client.id)}
                      />
                    </td>

                    <td className="px-4 py-3">
                      <Link
                        to={`/clients/${client.id}`}
                        className="font-medium text-accent-text hover:underline"
                      >
                        {client.name}
                      </Link>
                      {client.reference && (
                        <p className="text-xs text-ink-2">{client.reference}</p>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(client.tags ?? []).map((tag) => (
                          <span
                            key={tag.id}
                            className={`${tagPillClass(tagsById[tag.id] ?? tag)} px-2 py-1 text-caption`}
                          >
                            {tag.name}
                          </span>
                        ))}
                        {client.is_registered && (
                          <span className="rounded-full bg-accent-wash px-2 py-1 text-caption text-accent-text">
                            {t("clients.registered")}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-ink-2">
                      {client.phone_national && (
                        <span className="block" dir="ltr">
                          {client.phone_national}
                        </span>
                      )}
                      {client.email && (
                        <span className="block text-xs text-ink-2" dir="ltr">
                          {client.email}
                        </span>
                      )}
                      {!client.phone_national && !client.email && (
                        <span className="text-xs text-ink-2">{t("common.none")}</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-ink-2">{client.visits_count ?? 0}</td>

                    <td className="px-4 py-3 text-ink-2">
                      {client.last_visit_at ? (
                        formatDate(client.last_visit_at, i18n.language)
                      ) : (
                        <span className="text-xs text-ink-2">{t("clients.neverVisited")}</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-ink-2">{money(client)}</td>

                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <Link
                          to={`/clients/${client.id}`}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.view")}
                        </Link>
                        {mayUpdate && (
                          <button
                            type="button"
                            className="text-sm font-medium text-accent-text hover:underline"
                            onClick={() => {
                              setEditing(client);
                              setModalOpen(true);
                            }}
                          >
                            {t("common.edit")}
                          </button>
                        )}
                        {mayDelete && (
                          <button
                            type="button"
                            className="text-sm font-medium text-danger-text hover:underline"
                            onClick={() => {
                              setDeleteError("");
                              setDeleting(client);
                            }}
                          >
                            {t("common.delete")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* A cursor has no page number to show, so the pager says neither. */}
      {(!atStart || !atEnd) && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button type="button" className="btn-secondary" disabled={atStart} onClick={goPrevious}>
            {t("common.previous")}
          </button>
          {total != null && (
            <span className="text-sm text-ink-2">{t("clients.count", { count: total })}</span>
          )}
          <button type="button" className="btn-secondary" disabled={atEnd} onClick={goNext}>
            {t("common.next")}
          </button>
        </div>
      )}

      <ClientModal
        client={editing}
        tags={tags}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setToast({
            type: "success",
            message: wasEditing
              ? t("clients.updated", { name: saved.name })
              : t("clients.created", { name: saved.name }),
          });
          reload();
        }}
      />

      <ClientTagsModal
        open={tagsOpen}
        onClose={() => setTagsOpen(false)}
        onChanged={() => {
          loadTags();
          reload();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("clients.deleteTitle")}
        message={t("clients.deleteMessage", { name: deleting?.name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
